/**
 * CoverArt — book-cover image that fills its parent box, with graceful fallback.
 *
 * @layer-1 — use anywhere a title cover thumbnail/hero is rendered.
 *
 * Flow:
 *   - url present & loads OK              → <img object-cover>
 *   - url missing                         → default book-icon placeholder
 *   - url present but fails (404/corrupt) → default placeholder + red "broken" label
 *
 * The parent owns sizing, rounding, border and `overflow-hidden`; CoverArt fills
 * it via `w-full h-full`. The placeholder is transparent so it inherits the
 * parent's surface background (the app-wide "default cover" look).
 */
import { BookOpen } from "lucide-react";
import { useBrokenImage } from "@/shared/hooks/useBrokenImage";
import { BrokenImageBadge } from "./BrokenImageBadge";

interface CoverArtProps {
  /** Cover image URL. Empty/undefined renders the placeholder. */
  url?: string;
  /** Alt text — typically the title. */
  alt?: string;
  /** Placeholder book-icon size in px. Default 18. */
  iconSize?: number;
}

export function CoverArt({ url, alt, iconSize = 18 }: CoverArtProps) {
  const { showImage, broken, onError } = useBrokenImage(url);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {showImage ? (
        <img
          src={url}
          alt={alt ?? ""}
          className="w-full h-full object-cover"
          onError={onError}
        />
      ) : (
        <BookOpen size={iconSize} style={{ color: "var(--text-tertiary)" }} aria-hidden />
      )}

      {broken && <BrokenImageBadge />}
    </div>
  );
}
